import { XMarkIcon } from '@heroicons/react/20/solid';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import {
  EMPTY_QUEUE_FILTERS,
  FilterOptions,
  QueueFilterValues,
  queueFilterCount,
} from '../api/types';
import DateRangeSegment from './DateRangeSegment';
import FilterSegment, { SegmentOption } from './FilterSegment';

import useAuth from '@/hooks/useAuth';

// The approved action-queue filter bar (Figma node 38995:64197, GRAVY-10315).
//
// A full-width horizontal row beneath the queue heading: one funnel-anchored
// group of segmented dropdowns, with a right-aligned Reset.
//
//   [ ⌄funnel │ Client │ Skills │ Location │ Date range ]        ✕ Reset filters
//
// It replaces a narrow vertical popover anchored to the Show button that held
// every dimension as one scrolling checkbox list and applied on every click.
// GRAVY-10303 made those controls functional; this is the remaining interface
// gap, and it is the same gap GRAVY-10305 closed for the Calendar — so this bar
// is deliberately built from the SAME FilterSegment primitive rather than a
// parallel implementation. Calendar and queues now behave identically, which is
// the consistency the ticket asks for.
//
// Date range is not in the design node but is product-confirmed from
// GRAVY-10303, so it is a fourth segment in the same group — see
// DateRangeSegment.
//
// ## Client vs Billing Account (GRAVY-10316)
//
// A Client Admin's customer is FIXED by authorization, so a Client picker has
// nothing to offer them — every row already belongs to their one client. Where
// they do need to narrow is by property, i.e. Billing Account, which is what
// Dashboard V1 has always shown them (`BillingAccountPill` vs `ClientPill`).
//
// So the first control is Client for a Tenant Admin and Billing Account for a
// Client Admin. Only ONE of the two renders, matching V1 — showing both would
// imply a Client Admin can change the client they are looking at.
//
// ## Authorization
//
// Options come from /filters, already narrowed to the caller's authorized
// customers, and a selection can only ever NARROW the server-authorized scope
// (Dashboard::QueueFilters#effective_customer_ids for clients; for accounts the
// queue is already customer-scoped, so an account from another customer matches
// no authorized row). Nothing here can widen a Client Admin's scope, and the
// role only decides which CONTROL is drawn — never what the server permits.

type Props = {
  options?: FilterOptions;
  value: QueueFilterValues;
  onChange: (next: QueueFilterValues) => void;
  /** Distinguishes the three instances for tests and ARIA labelling. */
  queue: string;
};

const idOptions = (
  items: { id: number; name: string | null }[] | undefined,
): SegmentOption[] =>
  (items ?? []).map((i) => ({
    value: String(i.id),
    label: i.name || `#${i.id}`,
  }));

const toIds = (values: string[]): number[] => values.map(Number);
const toStrings = (ids: number[]): string[] => ids.map(String);

type SegmentId = 'client' | 'account' | 'skills' | 'location' | 'dates';

const QueueFilterBar = ({ options, value, onChange, queue }: Props) => {
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  const clientAdmin = isCurrentCustomerAdmin(currentAdmin);
  const activeCount = queueFilterCount(value);
  // At most one dropdown open, so opening a second closes the first instead of
  // stacking two panels over the queue rows.
  const [openId, setOpenId] = useState<SegmentId | null>(null);

  const set = (patch: Partial<QueueFilterValues>) =>
    onChange({ ...value, ...patch });

  const openProps = (id: SegmentId) => ({
    open: openId === id,
    onOpenChange: (next: boolean) => setOpenId(next ? id : null),
  });

  return (
    <div className="dashv2-fbar" data-testid={`queueFilterBar-${queue}`}>
      <div className="dashv2-fbar-groups">
        <div className="dashv2-fgroup">
          <span className="dashv2-fgroup-icon" aria-hidden="true">
            <FunnelIcon />
          </span>
          {clientAdmin ? (
            <FilterSegment
              label="Billing account"
              {...openProps('account')}
              options={idOptions(options?.accounts)}
              value={toStrings(value.accountIds)}
              onChange={(next) => set({ accountIds: toIds(next) })}
            />
          ) : (
            <FilterSegment
              label="Client"
              {...openProps('client')}
              options={idOptions(options?.clients)}
              value={toStrings(value.customerIds)}
              onChange={(next) => set({ customerIds: toIds(next) })}
            />
          )}
          <FilterSegment
            label="Skills"
            {...openProps('skills')}
            options={idOptions(options?.skills)}
            value={toStrings(value.skillIds)}
            onChange={(next) => set({ skillIds: toIds(next) })}
          />
          <FilterSegment
            label="Location"
            {...openProps('location')}
            options={idOptions(options?.regions)}
            value={toStrings(value.regionIds)}
            onChange={(next) => set({ regionIds: toIds(next) })}
          />
          <DateRangeSegment
            {...openProps('dates')}
            startDate={value.startDate}
            endDate={value.endDate}
            onChange={(next) => set(next)}
          />
        </div>
      </div>

      <button
        type="button"
        className="dashv2-freset"
        // Disabled with nothing applied, per the design's greyed state — a
        // Reset that does nothing reads as a broken control.
        disabled={activeCount === 0}
        onClick={() => {
          setOpenId(null);
          // Only THIS queue's filters. Each queue owns its own value in the
          // page, so a reset here cannot touch another queue's selections.
          onChange(EMPTY_QUEUE_FILTERS);
        }}
      >
        <XMarkIcon />
        Reset filters
      </button>
    </div>
  );
};

export default QueueFilterBar;
